import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "@usas/ui-web/wrappers/ReactRouterDom";

import validate from './MeetApprovedRequestApprovalValidation';

import { navLinks } from '../../../UseNavLinks';
import { createNewWorkflowObj } from '../utils/WorkflowsUtils';

import UseMeetApprovedRequestData from "../../../state/workflow/meetApprovedRequest/UseMeetApprovedRequestData";

import useMeetData from '@usas/ui-web/state/meet/UseMeetData';
import useMeetFileData from '@usas/ui-web/state/meetFile/UseMeetFileData';
import useEmbeddedReportData from '@usas/ui-web/state/embeddedReport/UseEmbeddedReportData';

import Constants from '@usas/ui-web/utils/Constants';
import useForm from '@usas/ui-web/utils/UseForm';

// Placeholders. Both go away once the meet report exists.
// The club report needs a real id to return rows, so this one is fixed.
const REPORT_ROUTE_NAME = 'WORKFLOWS_LSC_NEW_CLUB_REGISTRATION_APPROVALS';
const REPORT_PARAMETERS = { clubRegistrationId: 96169 };

const INITIAL_VIEW_STATE = {
  showReport: false,
  reportInfoId: -1,
  reportParameters: REPORT_PARAMETERS,
  routeName: ''
};

const useMeetApprovedRequestApproval = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { meetApprovedRequestState, getApprovedMeetWorkflow, putApprovedMeetWorkflow } = UseMeetApprovedRequestData();
  const { meetState, getMeet, putMeet } = useMeetData();
  const { getMeetFile } = useMeetFileData();
  const { getEmbeddedReportListForRoute } = useEmbeddedReportData();
  const { formState, errorState, onFormValueChanged, handleSubmit, setFormData, setIsDirty } = useForm(getInitialFormState, submitFormCallback, validate);
  const [meetApprovedRequestObjState, setMeetApprovedRequestObjState] = useState({});
  const [state, setState] = useState({ action: '', tryRedirect: false });
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);

  const onApprovedClicked = () => {
    setIsDirty(true);
    handleSubmit();
    setState({ ...state, action: 'Approve' });
  };

  const onRejectClicked = () => {
    setIsDirty(true);
    handleSubmit();
    setState({ ...state, action: 'Reject' });
  }

  const onBackClicked = () => {
    navigate(navLinks.MEET_APPROVED_REQUEST, { state: { isRevisit: location.state?.isRevisit } });
  };

  const onDownloadUploadedFile = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    showUploadedFile(meetState.objData.meetAnnouncementUrl);
  };

  const onViewReportClicked = () => {
    setViewState({
      ...viewState,
      routeName: REPORT_ROUTE_NAME
    });
  };

  useEffect(() => {
    if (viewState.routeName) {
      const reportListPromise = getEmbeddedReportListForRoute(viewState.routeName);

      if (reportListPromise) {
        reportListPromise.then((stateObj) => {
          if (Array.isArray(stateObj.arrayData)) {
            const listNames = [];
            stateObj.arrayData.forEach(x => {
              if (listNames.filter(a => a.id === x.listName).length === 0) {
                listNames.push({ id: x.listName, name: x.listDisplayName });
              }
            });

            setViewState({
              ...viewState,
              isLoading: false,
              showGrid: false,
              showReport: stateObj.arrayData.length === 1,
              reportInfoId: stateObj.arrayData.length === 1 ? stateObj.arrayData[0].reportInfoId : -1,
              listNames,
              rawReportList: [...stateObj.arrayData],
              reportList: [...stateObj.arrayData]
            });
          } else {
            setViewState({
              ...viewState,
              isLoading: false,
              showGrid: false,
              showReport: false,
              reportInfoId: -1,
              listNames: [],
              rawReportList: [],
              reportList: []
            });
          }
        }).catch(() => {
          setViewState({
            ...viewState,
            isLoading: false,
            showGrid: false,
            showReport: false,
            reportInfoId: -1,
            listNames: [],
            rawReportList: [],
            reportList: []
          });
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewState.routeName]);

  useEffect(() => {
    if (location.state?.meetAppReq?.workflowId > 0) {
      setMeetApprovedRequestObjState(location.state.meetAppReq);
      getApprovedMeetWorkflow(location.state.meetAppReq.workflowId);
      getMeet(location.state.meetAppReq.meetId);

      setIsDirty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (meetState.isObjLoaded === true) {
      setFormData({
        ...formState,
        meetName: meetState.objData.meetName || ''
        // meetSanctionNumber: meetState.objData.meetSanctionNumber || ''
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetState.isObjLoaded]);

  useEffect(() => {
    if (meetState.isSaved === true && state.action !== '') {
      if (state.action === 'Approve') {
        const workflowObjCopy = JSON.parse(JSON.stringify(meetApprovedRequestState.objData));
        putApprovedMeetWorkflow(meetApprovedRequestObjState.workflowId,
          createNewWorkflowObj(workflowObjCopy, meetApprovedRequestObjState.workflowStepId, Constants.WORKFLOW_STATUS_APPROVED,
            '', 0));
        setState({ ...state, action: '', tryRedirect: true });
      } else if (state.action === 'Reject') {
        const workflowObjCopy = JSON.parse(JSON.stringify(meetApprovedRequestState.objData));
        putApprovedMeetWorkflow(meetApprovedRequestObjState.workflowId,
          createNewWorkflowObj(workflowObjCopy, meetApprovedRequestObjState.workflowStepId, Constants.WORKFLOW_STATUS_REJECTED,
            '', 0));
        setState({ ...state, action: '', tryRedirect: true });
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetState.isSaved, state.action]);

  useEffect(() => {
    if (meetState.isSaved === true && meetApprovedRequestState.isSaved === true && state.tryRedirect === true) {
      navigate(navLinks.MEET_APPROVED_REQUEST, { state: { isRevisit: location.state?.isRevisit } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetState.isSaved, meetApprovedRequestState.isSaved, state.tryRedirect]);

  async function showUploadedFile(fileUrl) {
    if (fileUrl) {
      const getFileResult = await getMeetFile(fileUrl);

      if (getFileResult) {
        if (getFileResult.objData?.blobFile?.size > 0) {
          var file = window.URL.createObjectURL(getFileResult.objData?.blobFile);
          const link = document.createElement('a');

          link.href = file;
          link.setAttribute('target', '_blank');
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        }
      }
    }
  }

  function submitFormCallback() {
    try {
      const meetObjCopy = JSON.parse(JSON.stringify(meetState.objData));
      meetObjCopy.meetName = formState.meetName.trim();
      // meetObjCopy.meetSanctionNumber = formState.meetSanctionNumber.trim();

      putMeet(meetObjCopy.meetId, -1, '', '', meetObjCopy);
      // putMeet(meetObjCopy.meetId, meetObjCopy);
    } catch (e) {
      console.error('Error in submitFormCallback: ' + (e && e.message ? e.message : 'Unknown error'));
    }
  }

  function getInitialFormState() {
    return {
      meetName: ''
      // meetSanctionNumber: '',
    };
  }

  return {
    location,
    Constants,
    isSaving: meetApprovedRequestState.isSaving || meetState.isSaving,
    isLoading: meetApprovedRequestState.isObjLoading || meetState.isObjLoading,
    hasMeetAnnouncement: meetState.objData?.meetAnnouncementUrl ? true : false,
    meetApprovedRequestState,
    meetApprovedRequestObjState,
    viewState,
    setViewState,
    INITIAL_VIEW_STATE,
    onApprovedClicked,
    onRejectClicked,
    onBackClicked,
    onDownloadUploadedFile,
    onViewReportClicked,
    formState,
    errorState,
    onFormValueChanged
  };
};

export default useMeetApprovedRequestApproval;